import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HoverColor, IconColor, IconSize } from '@freelancer/ui/icon';
import {
  LinkColor,
  LinkHoverColor,
  LinkUnderline,
  QueryParams,
} from '@freelancer/ui/link';
import { ListItemPadding, ListItemType } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { FontType, TextSize } from '@freelancer/ui/text';

export interface SplitButtonOption {
  copy: string;
  link?: string;
  linkQueryParams?: QueryParams;
  iconName?: string;
}

export enum SplitButtonOptionPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
}

@Component({
  selector: 'fl-split-button',
  template: `
    <fl-bit class="SplitButtonContainer" #cardOptions>
      <fl-bit class="ButtonGroup" *ngIf="options?.length > 0">
        <fl-button
          class="PrimaryButton"
          [color]="color"
          [display]="display"
          [link]="options[0].link"
          [queryParams]="options[0].linkQueryParams"
          [size]="size"
          [busy]="busy"
          [buttonGroupFirst]="options.length > 1 && !busy"
          (click)="primaryClicked.emit(); hideOptions($event)"
        >
          <ng-content> </ng-content>
        </fl-button>
        <fl-button
          class="ChevronButton"
          *ngIf="options.length > 1 && !busy"
          title="More Options"
          i18n-title="More Options"
          [color]="color"
          [size]="size"
          [buttonGroupLast]="true"
          (click)="toggleShouldShowOptions()"
        >
          <fl-icon
            class="ChevronButtonIcon"
            [name]="chevronType"
            [size]="iconSize"
            [color]="chevronColor"
          ></fl-icon>
        </fl-button>
      </fl-bit>

      <fl-card
        #dropdown
        class="OptionListCard"
        *ngIf="shouldShowOptions"
        [edgeToEdge]="true"
        [maxContent]="false"
        [attr.data-position]="position"
      >
        <ul>
          <ng-container *ngFor="let option of options.slice(1)">
            <fl-text *ngIf="!option.link" [size]="fontSize">
              <ng-container
                *ngTemplateOutlet="content; context: { $implicit: option }"
              ></ng-container>
            </fl-text>

            <fl-link
              *ngIf="option.link"
              [color]="LinkColor.INHERIT"
              [hoverColor]="LinkHoverColor.INHERIT"
              [link]="option.link"
              [queryParams]="option.linkQueryParams"
              [size]="fontSize"
              [underline]="LinkUnderline.NEVER"
            >
              <ng-container
                *ngTemplateOutlet="content; context: { $implicit: option }"
              ></ng-container>
            </fl-link>
          </ng-container>
        </ul>
      </fl-card>
    </fl-bit>

    <ng-template #content let-option>
      <fl-list-item
        [clickable]="true"
        [type]="ListItemType.NON_BORDERED"
        [padding]="optionPadding"
        (click)="optionSelected.emit(option); hideOptions($event)"
      >
        <fl-bit class="OptionListItem">
          <fl-icon
            *ngIf="option.iconName"
            [name]="option.iconName"
            [size]="iconSize"
            [flMarginRight]="Margin.XXSMALL"
          ></fl-icon>
          {{ option.copy }}
        </fl-bit>
      </fl-list-item>
    </ng-template>
  `,
  styleUrls: ['./split-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplitButtonComponent implements OnInit {
  Margin = Margin;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  TextSize = TextSize;
  FontType = FontType;
  HoverColor = HoverColor;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LinkUnderline = LinkUnderline;
  ListItemType = ListItemType;

  iconSize = IconSize.MID;
  chevronType: 'ui-chevron-down' | 'ui-chevron-up' = 'ui-chevron-down';
  chevronColor: IconColor;
  shouldShowOptions = false;
  position = SplitButtonOptionPosition.BOTTOM;

  get fontSize() {
    switch (this.size) {
      case ButtonSize.MINI:
        return TextSize.XXSMALL;
      case ButtonSize.SMALL:
        return TextSize.XSMALL;
      case ButtonSize.LARGE:
      case ButtonSize.XLARGE:
      case ButtonSize.XXLARGE:
        return TextSize.SMALL;
      default:
        return undefined;
    }
  }

  get optionPadding() {
    switch (this.size) {
      case ButtonSize.MINI:
        return ListItemPadding.XXSMALL;
      case ButtonSize.SMALL:
        return ListItemPadding.XSMALL;
      case ButtonSize.LARGE:
      case ButtonSize.XLARGE:
      case ButtonSize.XXLARGE:
        return ListItemPadding.SMALL;
      default:
        return undefined;
    }
  }

  @Input() color: ButtonColor;
  @Input() options: SplitButtonOption[];
  @Input() busy: boolean;

  @HostBinding('attr.data-display')
  @Input()
  display: 'block' | 'inline' = 'inline';

  @HostBinding('attr.data-option-font-size')
  @Input()
  size: ButtonSize;

  @Output() primaryClicked = new EventEmitter<void>();
  @Output() optionSelected = new EventEmitter<SplitButtonOption>();

  @ViewChild('cardOptions', { read: ElementRef })
  cardOptions: ElementRef<HTMLElement>;

  @ViewChild('dropdown', { read: ElementRef })
  dropdown: ElementRef<HTMLElement>;

  constructor(
    private timeUtils: TimeUtils,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  // Listening for clicks outside the input component
  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent) {
    if (
      event &&
      !this.cardOptions.nativeElement.contains(event.target as HTMLElement) &&
      this.shouldShowOptions
    ) {
      this.toggleShouldShowOptions();
    }
  }

  ngOnInit() {
    this.iconSize = [ButtonSize.MINI, ButtonSize.SMALL].includes(this.size)
      ? IconSize.XSMALL
      : IconSize.SMALL;
    this.chevronColor = [
      ButtonColor.DEFAULT,
      ButtonColor.TRANSPARENT_DARK,
    ].includes(this.color)
      ? IconColor.DARK
      : IconColor.LIGHT;
  }

  hideOptions(e: MouseEvent) {
    e.stopPropagation();
    this.shouldShowOptions = false;
    this.updateChevron();
    this.setDropdownPosition();
  }

  toggleShouldShowOptions() {
    this.shouldShowOptions = !this.shouldShowOptions;
    this.updateChevron();

    // Needs setTimeout so getBoundingClientRect returns the right values
    this.timeUtils.setTimeout(() => {
      this.setDropdownPosition();
    });
  }

  updateChevron() {
    this.chevronType = this.shouldShowOptions
      ? 'ui-chevron-up'
      : 'ui-chevron-down';
  }

  setDropdownPosition() {
    if (!this.dropdown) {
      return;
    }

    this.position = SplitButtonOptionPosition.BOTTOM;
    this.changeDetectorRef.detectChanges();

    if (this.shouldShowOptions) {
      const dropdownPosition =
        this.dropdown.nativeElement.getBoundingClientRect().top +
        this.dropdown.nativeElement.getBoundingClientRect().height;

      this.position =
        dropdownPosition > window.innerHeight
          ? SplitButtonOptionPosition.TOP
          : SplitButtonOptionPosition.BOTTOM;

      this.changeDetectorRef.detectChanges();
    }
  }
}
